import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ROLES } from '@shared/types';
import type { Attachment } from '@shared/types';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useToast } from '../lib/toast';
import { AttachmentUploader } from '../components/Attachments';
import { Field, Tag } from '../components/ui';
import { PageHeader } from '../components/PageHeader';

export function ProfilePage() {
  const { user, setUser, logout, isBoard, community, isSuperAdmin, isTenant } = useAuth();
  const { notify, fail } = useToast();

  const [profile, setProfile] = useState({
    name: user?.name ?? '',
    unit: user?.unit ?? '',
    phone: user?.phone ?? '',
  });
  const [avatar, setAvatar] = useState<Attachment[]>([]);
  const [savingProfile, setSavingProfile] = useState(false);

  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [savingPassword, setSavingPassword] = useState(false);

  if (!user) return null;

  async function saveProfile(event: React.FormEvent) {
    event.preventDefault();
    setSavingProfile(true);
    try {
      const { user: updated } = await api.updateProfile({
        name: profile.name,
        unit: profile.unit || null,
        phone: profile.phone || null,
        ...(avatar.length > 0 ? { avatarAttachmentId: avatar[0].id } : {}),
      });
      setUser(updated);
      setAvatar([]);
      notify('Profile saved.', 'success');
    } catch (error) {
      fail(error, 'Your profile was not saved.');
    } finally {
      setSavingProfile(false);
    }
  }

  async function savePassword(event: React.FormEvent) {
    event.preventDefault();
    if (passwords.next !== passwords.confirm) {
      notify('The new passwords do not match.', 'error');
      return;
    }
    setSavingPassword(true);
    try {
      await api.changePassword(passwords.current, passwords.next);
      setPasswords({ current: '', next: '', confirm: '' });
      notify('Password updated.', 'success');
    } catch (error) {
      fail(error, 'Your password was not changed.');
    } finally {
      setSavingPassword(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Your profile"
        lede="Your name and address appear on anything you submit, so the board knows who to follow up with."
      />

      <div className="card">
        <div className="row between" style={{ marginBottom: 12 }}>
          <div className="row" style={{ gap: 12 }}>
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt=""
                style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              <span className="logo" style={{ width: 52, height: 52, fontSize: 18 }}>
                {user.name.slice(0, 1).toUpperCase()}
              </span>
            )}
            <div>
              <h3 style={{ marginBottom: 2 }}>{user.name}</h3>
              <div className="mut">{user.email}</div>
            </div>
          </div>
          <div className="tag-row">
            {user.isTenant ? (
              <Tag tone="warn">Tenant</Tag>
            ) : user.communityId ? (
              <Tag tone="ok">Owner</Tag>
            ) : null}
            {user.roles.map((role) => (
              <Tag key={role} tone={role === 'member' ? 'neutral' : 'info'}>
                {ROLES[role] ?? role}
              </Tag>
            ))}
          </div>
        </div>

        <form onSubmit={saveProfile}>
          <Field label="Display name">
            <input
              value={profile.name}
              onChange={(event) => setProfile({ ...profile, name: event.target.value })}
              required
            />
          </Field>
          <Field label="Address or unit">
            <input
              value={profile.unit}
              onChange={(event) => setProfile({ ...profile, unit: event.target.value })}
              placeholder="18 Willow Drive"
            />
          </Field>
          <Field label="Phone">
            <input
              type="tel"
              inputMode="tel"
              value={profile.phone}
              onChange={(event) => setProfile({ ...profile, phone: event.target.value })}
            />
          </Field>

          <span className="label-text">Profile photo</span>
          <AttachmentUploader
            value={avatar}
            onChange={(items) => setAvatar(items.slice(-1))}
            accept="image/*"
            label="Choose photo"
            max={1}
          />

          <div className="btn-row">
            <button type="submit" className="btn" disabled={savingProfile}>
              {savingProfile ? 'Saving…' : 'Save profile'}
            </button>
          </div>
        </form>
      </div>

      <div className="card" id="password">
        <h3>Change password</h3>
        <form onSubmit={savePassword}>
          <Field label="Current password">
            <input
              type="password"
              autoComplete="current-password"
              value={passwords.current}
              onChange={(event) => setPasswords({ ...passwords, current: event.target.value })}
              required
            />
          </Field>
          <Field label="New password" hint="At least 8 characters.">
            <input
              type="password"
              autoComplete="new-password"
              value={passwords.next}
              onChange={(event) => setPasswords({ ...passwords, next: event.target.value })}
              required
              minLength={8}
            />
          </Field>
          <Field label="Confirm new password">
            <input
              type="password"
              autoComplete="new-password"
              value={passwords.confirm}
              onChange={(event) => setPasswords({ ...passwords, confirm: event.target.value })}
              required
              minLength={8}
            />
          </Field>
          <div className="btn-row">
            <button type="submit" className="btn s" disabled={savingPassword}>
              {savingPassword ? 'Updating…' : 'Update password'}
            </button>
          </div>
        </form>
      </div>

      {community && (
        <div className="card">
          <h3>{community.name}</h3>
          {community.address && <p className="mut">{community.address}</p>}
          <p className="mut">
            {isTenant
              ? 'You are signed in as a tenant. You can view events and property violations.'
              : 'You are signed in as an owner. You can submit architectural review requests, view events, and property violations.'}
          </p>
          <p className="mut" style={{ margin: 0 }}>
            {[
              community.boardContactName,
              community.boardEmail,
              community.boardContactPhone,
              community.managementEmail ? `Management: ${community.managementEmail}` : null,
              community.managementPhone,
            ]
              .filter(Boolean)
              .join(' · ') || 'Board contacts have not been filled in yet.'}
          </p>
        </div>
      )}

      {isBoard && (
        <div className="card">
          <h3>Board administration</h3>
          <p className="mut">Manage who can access the board, review board and organizer areas.</p>
          <div className="btn-row">
            <Link className="btn s" to="/board/residents">
              Member Management
            </Link>
            <Link className="btn s" to="/community">
              Community Management
            </Link>
            <Link className="btn s" to="/architecture/guidelines">
              Architectural guidelines
            </Link>
            <Link className="btn s" to="/bylaws">
              By Laws folder
            </Link>
          </div>
        </div>
      )}

      {isSuperAdmin && (
        <div className="card">
          <h3>Platform</h3>
          <p className="mut">Create communities and invite HOA boards.</p>
          <div className="btn-row">
            <Link className="btn s" to="/admin">
              Super Admin
            </Link>
          </div>
        </div>
      )}

      <div className="card">
        <div className="btn-row" style={{ marginTop: 0 }}>
          <button type="button" className="btn danger" onClick={() => void logout()}>
            Sign off
          </button>
        </div>
      </div>
    </div>
  );
}
